import AddressTypes from "@/utils/AddressTypes";
import type { Address } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";

// Clave normalizada: mayúsculas, sin tildes y sin la puntuación final de las abreviaturas ("C/", "Avda.").
function normalizeRoadTypeKey(value: string): string
{
    return value.trim().toUpperCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[./\s]+$/, "").trim();
}

// Abreviaturas presentes en producción (2026-09-30) que no coinciden ni con el código ni con el nombre
// del catálogo: la forma de cuatro letras del callejero, en castellano, catalán, euskera y gallego. Se
// comparan ya normalizadas, así que "URBN." y "URBN" son la misma clave. Sin equivalencia en el
// catálogo se quedan fuera a propósito (el usuario elige): CJTO (Conjunto), GRVA (Gran Vía), K., y los
// que son en realidad parte del nombre de la vía (MADRID, JOSE, HIJDIA). Hasta la unificación de
// direcciones.
const ROAD_TYPE_ALIASES: Record<string, string> =
{
    // Calle
    C: "CL",
    CRER: "CL",     // Carrer
    RUA: "CL",      // Rúa
    // Avenida
    AVDA: "AV",
    AVGD: "AV",     // Avinguda
    // Barrio
    AUZO: "BO",     // Auzoa
    BARO: "BO",
    // Camino
    CMNO: "CM",
    CAMI: "CM",     // Camí
    // Carretera
    CRTA: "CR",
    // Grupo
    GRPO: "GR",
    // Lugar
    LGAR: "LG",
    // Pasaje
    PSTG: "PJ",     // Passatge
    // Paseo
    PSEO: "PS",
    // Plaza
    PLZA: "PZ",
    // Polígono
    POL: "PG",
    // Travesía
    TSIA: "TR",     // Travessia
    // Urbanización
    URBN: "UR",
};

// Código, nombre y alias del catálogo apuntando al código que usa el selector.
const ROAD_TYPE_CODES_BY_KEY = new Map<string, string>([
    ...AddressTypes.map((t): [string, string] => [normalizeRoadTypeKey(t.value), t.value]),
    ...AddressTypes.map((t): [string, string] => [normalizeRoadTypeKey(t.text), t.value]),
    ...Object.entries(ROAD_TYPE_ALIASES),
]);

/**
 * Traduce el tipo de vía guardado al código del selector ("Calle", "CALLE", "CL" -> "CL"; "URBN" -> "UR").
 * Devuelve null si no hay equivalencia. Solo sirve para mostrar y validar: el valor guardado no se
 * reescribe mientras el usuario no cambie el selector, porque la dirección forma parte de la huella del
 * KYC y reescribirla sin más invalidaría el KYC firmado de un interviniente ACTIVE.
 */
export function resolveRoadTypeCode(roadType: string | null | undefined): string | null
{
    if(!roadType)
    {
        return null;
    }
    return ROAD_TYPE_CODES_BY_KEY.get(normalizeRoadTypeKey(roadType)) ?? null;
}

/**
 * Indica si el tipo de vía tiene equivalencia en el catálogo del selector. Uno sin equivalencia se ve
 * como "-- Seleccionar --", así que cuenta como pendiente.
 */
export function isKnownRoadType(roadType: string | null | undefined): boolean
{
    return resolveRoadTypeCode(roadType) !== null;
}

/**
 * Validez de una dirección (postal o fiscal). Única fuente para el alta y la edición: antes cada camino
 * tenía su copia y la de edición no exigía el tipo de vía, así que se podía finalizar una edición con
 * un campo obligatorio vacío en pantalla que el alta sí bloqueaba.
 *
 * Obligatoria y completa para TODOS los intervinientes, menores incluidos. Aunque las reglas de
 * validación del backend (UserIdentitiesBusinessRules, CanBeSynchronizedWithFinametrix) omiten la
 * dirección con IsMinor, la integración con Finametrix la necesita para cada titular del plan:
 * SyncClientWithFinametrix y CreateContractOnFinametrix leen PostalAddress sin comprobar nulos y hacen
 * int.Parse del código postal. Un menor sin dirección no se puede dar de alta allí.
 */
export function isAddressValid(a: Address | null | undefined): boolean
{
    return !!(a && isKnownRoadType(a.roadType) && a.streetName && a.streetNumber && a.zipCode && a.city && a.state && a.country);
}
